<template>
  <div class="space-y-2">
    <!-- Ampliación de una orden existente: copia sus valores y la referencia. -->
    <div v-if="projectId" class="flex-grow">
      <b-switch v-model="isExtension" @input="onToggleExtension">{{ $t('is_work_order_extension') }}</b-switch>
    </div>

    <div v-if="projectId && isExtension" class="flex-grow">
      <label class="w-full text-xs font-semibold text-gray-600">{{ $t('extends_work_order') }}</label>
      <multiselect
        v-model="sourceOrder"
        :options="sourceOptions"
        label="code"
        track-by="id"
        :custom-label="sourceLabel"
        :show-labels="false"
        :searchable="true"
        :allow-empty="true"
        :placeholder="$t('select')"
        :loading="isLoadingWorkOrders"
        @input="onSourceSelected"
      >
        <template slot="noResult">{{ $t('no_elements_found') }}</template>
        <template slot="noOptions">{{ $t('no_options_available') }}</template>
      </multiselect>
    </div>

    <div v-if="projectId" class="flex-grow">
      <label class="w-full text-xs font-semibold text-gray-600">{{ $t('subcontractor') }}</label>
      <multiselect
        v-model="selectedPartner"
        :options="partners"
        label="name"
        track-by="id"
        :show-labels="false"
        :searchable="true"
        :allow-empty="true"
        :placeholder="$t('select')"
        :loading="isLoadingPartners"
      >
        <template slot="noResult">{{ $t('no_elements_found') }}</template>
        <template slot="noOptions">{{ $t('no_options_available') }}</template>
      </multiselect>
    </div>

    <div class="flex-grow">
      <label for="code" class="w-full text-xs font-semibold text-gray-600">{{ $t('code') }}</label>
      <v-input
        v-model="item.code"
        title="code"
        id="code"
        name="code"
        :placeholder="codePlaceholder"
        class="w-full"
        required
      />
    </div>

    <div class="flex-grow">
      <label for="description" class="w-full text-xs font-semibold text-gray-600">{{ $t('description') }}</label>
      <v-input
        v-model="item.description"
        title="description"
        id="description"
        name="description"
        placeholder="description"
        class="w-full"
      />
    </div>

    <div class="flex-grow">
      <label class="w-full text-xs font-semibold text-gray-600">{{ $t('certification_flow') }}</label>
      <multiselect
        v-model="selectedFlow"
        :options="flows"
        label="name"
        track-by="id"
        :custom-label="flowLabel"
        :show-labels="false"
        :searchable="true"
        :allow-empty="true"
        :placeholder="$t('select')"
        :loading="isLoadingFlows"
      >
        <template slot="noResult">{{ $t('no_elements_found') }}</template>
        <template slot="noOptions">{{ $t('no_options_available') }}</template>
      </multiselect>
    </div>

    <div class="flex gap-3">
      <div class="flex-grow">
        <label for="advance_percentage" class="w-full text-xs font-semibold text-gray-600">{{ $t('advance_percentage') }}</label>
        <v-input
          v-model="item.advance_percentage"
          title="advance_percentage"
          id="advance_percentage"
          name="advance_percentage"
          type="number"
          step="any"
          min="0"
          max="100"
          class="w-full"
        />
      </div>
    </div>

    <div class="flex-grow w-full">
      <label for="upload-file" class="w-full text-xs font-semibold text-gray-600">Documento de pedido</label>

      <b-field class="file is-success">
        <b-upload
          v-model="item.file"
          :accept="accept"
          :expanded="true"
        >
          <a class="button is-primary is-fullwidth">
            <b-icon class="file-icon" icon="upload" pack="fas"></b-icon>
            <span>{{ item.file ? item.file.name : $t("upload_document") }}</span>
          </a>
        </b-upload>
      </b-field>
    </div>

  </div>
</template>

<script>
  import axios from 'axios'
  import Multiselect from 'vue-multiselect'

  const CERTIFICATION_FLOW_TYPE = 3

  export default {
    name: 'work-orders-form',

    components: {
      Multiselect,
    },

    props: {
      data: {type: Object, required: true},
      projectId: {type: Number, required: false, default: null},
    },

    data: function() {
      return {
        item: {
          id: this.data.id,
          code: this.data.code,
          description: this.data.description,
          partner_id: this.data.partner_id || null,
          flow_id: this.data.flow_id || null,
          advance_percentage: this.data.advance_percentage ?? null,
          extends_work_order_id: this.data.extends_work_order_id || null,
          file: null
        },
        partners: [],
        flows: [],
        isLoadingPartners: false,
        isLoadingFlows: false,
        accept: '',
        // Ampliación
        isExtension: !!(this.data.extends_work_order_id),
        workOrders: [],
        isLoadingWorkOrders: false,
        sourceOrder: null,
        // Código de ampliación sugerido: se muestra en el placeholder, NO autocompleta.
        suggestedCode: '',
      }
    },

    computed: {
      selectedPartner: {
        get() {
          return this.partners.find(p => Number(p.id) === Number(this.item.partner_id)) || null
        },
        set(value) {
          this.item.partner_id = value ? value.id : null
        },
      },
      selectedFlow: {
        get() {
          return this.flows.find(f => Number(f.id) === Number(this.item.flow_id)) || null
        },
        set(value) {
          this.item.flow_id = value ? value.id : null
        },
      },
      // Órdenes que se pueden ampliar: excluye la propia (en edición) y las que ya
      // son ampliaciones (solo se amplían órdenes base, no otras ampliaciones).
      sourceOptions() {
        return this.workOrders.filter(o =>
          Number(o.id) !== Number(this.item.id) && !o.extends_work_order_id
        )
      },
      // Placeholder del código: sugerencia de ampliación (no autocompleta el campo,
      // que sigue siendo obligatorio y en blanco hasta que el usuario lo confirme).
      codePlaceholder() {
        return this.suggestedCode || this.$t('code')
      },
    },

    watch: {
      data: {
        deep: true,
        handler(value) {
          this.item = {
            id: value.id,
            code: value.code,
            description: value.description,
            partner_id: value.partner_id || null,
            flow_id: value.flow_id || null,
            advance_percentage: value.advance_percentage ?? null,
            extends_work_order_id: value.extends_work_order_id || null,
            file: null,
          }
          this.isExtension = !!value.extends_work_order_id
          this.sourceOrder = null
          this.suggestedCode = ''
          if (this.isExtension) {
            this.loadWorkOrders()
          }
        },
      },
    },

    created() {
      this.accept = this.$acceptedExtensions
      this.loadPartners()
      this.loadFlows()
      if (this.isExtension) {
        this.loadWorkOrders()
      }
    },

    methods: {
      flowLabel(flow) {
        if (!flow) return ''
        return flow.code ? `${flow.code} — ${flow.name}` : flow.name
      },

      async loadPartners() {
        if (!this.projectId) return
        this.isLoadingPartners = true
        try {
          const { data } = await axios.get(`/api/v2/project/${this.projectId}/partners`)
          this.partners = Array.isArray(data?.data) ? data.data
            : Array.isArray(data?.partners) ? data.partners
            : (Array.isArray(data) ? data : [])
        } catch (e) {
          this.partners = []
        } finally {
          this.isLoadingPartners = false
        }
      },

      async loadFlows() {
        if (!this.projectId) return
        this.isLoadingFlows = true
        try {
          const { data } = await axios.get(`/api/v2/project/${this.projectId}/validations-templates`, {
            params: {
              type: CERTIFICATION_FLOW_TYPE,
            },
          })
          this.flows = data.templates || []
        } catch (e) {
          this.flows = []
        } finally {
          this.isLoadingFlows = false
        }
      },

      // --- Ampliación de orden ---
      sourceLabel(order) {
        if (!order) return ''
        return order.description ? `${order.code} — ${order.description}` : order.code
      },

      async loadWorkOrders() {
        if (!this.projectId) return
        this.isLoadingWorkOrders = true
        try {
          const { data } = await axios.get('/api/v2/work-orders', {
            params: { project_id: this.projectId, with_partners: 1 },
          })
          this.workOrders = Array.isArray(data?.data) ? data.data : []
          // En edición: preselecciona la orden origen ya guardada.
          if (this.item.extends_work_order_id) {
            this.sourceOrder = this.workOrders.find(
              o => Number(o.id) === Number(this.item.extends_work_order_id)
            ) || null
          }
        } catch (e) {
          this.workOrders = []
        } finally {
          this.isLoadingWorkOrders = false
        }
      },

      onToggleExtension(active) {
        if (active) {
          this.loadWorkOrders()
        } else {
          // Desactivar: deja de ser ampliación (no borra los valores ya copiados).
          this.sourceOrder = null
          this.item.extends_work_order_id = null
          this.suggestedCode = ''
        }
      },

      // Al elegir la orden origen: copia sus valores (menos código/documento) y sugiere
      // el código SOLO en el placeholder (no autocompleta el campo).
      onSourceSelected(order) {
        if (!order) {
          this.item.extends_work_order_id = null
          this.suggestedCode = ''
          return
        }
        this.item.extends_work_order_id = order.id

        const partner = Array.isArray(order.partners) && order.partners.length ? order.partners[0] : null
        this.item.partner_id = partner ? partner.id : null
        this.item.flow_id = partner && partner.pivot ? (partner.pivot.flow_id || null) : null
        this.item.advance_percentage = partner && partner.pivot ? (partner.pivot.advance_percentage ?? null) : null
        this.item.description = order.description || ''
        // Código sugerido: se muestra en el placeholder; el campo queda en blanco y
        // obligatorio para que el usuario lo escriba/confirme.
        this.suggestedCode = this.generateExtensionCode(order.code)
      },

      // Sugiere el siguiente código de ampliación: <base>-A1, -A2, ... (según las existentes).
      generateExtensionCode(baseCode) {
        const base = String(baseCode || '')
        if (!base) return ''
        const escaped = base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const re = new RegExp('^' + escaped + '-A(\\d+)$')
        let max = 0
        this.workOrders.forEach((o) => {
          const m = String(o.code || '').match(re)
          if (m) max = Math.max(max, parseInt(m[1], 10))
        })
        return `${base}-A${max + 1}`
      },

      getDataForm() {
        return this.item
      }
    }
  };
</script>

<style lang="scss" scoped>
/* Compactado mínimo de campos, al nivel del formulario "Generar certificación" (~32px). */
::v-deep .multiselect__tags {
  min-height: 32px;
  padding: 4px 40px 0 8px;
  font-size: 0.8rem;
}

::v-deep .multiselect__single,
::v-deep .multiselect__placeholder,
::v-deep .multiselect__input {
  font-size: 0.8rem;
  min-height: 20px;
  margin-bottom: 4px;
  padding-top: 2px;
}

::v-deep .multiselect__select {
  height: 32px;
}

::v-deep input {
  height: 32px;
  font-size: 0.8rem;
}
</style>
